import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Package, Receipt, Truck } from 'lucide-react';
import { Button } from '../components/Button';
import { OrderStatusBadge } from '../components/OrderStatusBadge';
import { PageHeader } from '../components/PageHeader';
import { usePlatform } from '../contexts/PlatformContext';
import { formatAED } from '../utils/pack';
import { formatOrderDate } from '../utils/orders';
type Filter = 'all' | 'active' | 'delivered' | 'cancelled';
const filters: {
  id: Filter;
  label: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'active',
  label: 'Active'
}, {
  id: 'delivered',
  label: 'Delivered'
}, {
  id: 'cancelled',
  label: 'Cancelled'
}];
export function Orders() {
  const {
    orders
  } = usePlatform();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const shown = orders.filter(o => {
    if (filter === 'active') return o.status === 'Processing' || o.status === 'In transit';
    if (filter === 'delivered') return o.status === 'Delivered';
    if (filter === 'cancelled') return o.status === 'Cancelled';
    return true;
  });
  const paid = orders.filter(o => o.status !== 'Cancelled');
  const totalSpent = paid.reduce((sum, o) => sum + o.total, 0);
  const active = orders.find(o => o.status === 'Processing' || o.status === 'In transit');
  const stats = [{
    icon: Receipt,
    label: 'Total spent',
    value: formatAED(totalSpent)
  }, {
    icon: Package,
    label: 'Packs received',
    value: `${orders.filter(o => o.status === 'Delivered').length}`
  }, {
    icon: Truck,
    label: 'Current shipment',
    value: active ? active.status : 'None'
  }];
  return <div data-id="element-290" data-mp="1rpug0jy8de3d">
      <PageHeader eyebrow="Account" title="Orders & transactions" description="Every monthly pack you've ordered, with its contents, payment and delivery." actions={<Button size="lg" className="bg-[#9b8cf0] text-[#0f1017] hover:bg-[#b0a4f5]" onClick={() => navigate('/plan')} data-id="element-292" data-mp="z0f3fpqx3d5c">
            Order next pack
          </Button>} data-id="element-291" data-mp="1rp2uy329eyrc" />

      <div className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-3" data-id="element-293" data-mp="341znk3jprmww">
        {stats.map(s => <div key={s.label} className="flex items-center gap-4 bg-[#0f1017] p-5" data-id="element-294" data-mp="38vm2619fu0vc">
            <s.icon className="size-5 text-[#9b8cf0]" data-id="element-295" data-mp="3cuberrmnjedx" />
            <div data-id="element-296" data-mp="2g5dlyeygpqje">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-id="element-297" data-mp="2g5vkxjcieiv0">{s.label}</p>
              <p className="mt-1 text-lg font-medium" data-id="element-298" data-mp="2l4gp1tuvtmoq">{s.value}</p>
            </div>
          </div>)}
      </div>

      <div className="mt-8 flex gap-1 overflow-x-auto" role="group" aria-label="Filter orders" data-id="element-299" data-mp="2p7ms6smp6xqz">
        {filters.map(f => <button key={f.id} aria-pressed={filter === f.id} onClick={() => setFilter(f.id)} className={`shrink-0 rounded-full px-4 py-1.5 text-sm transition-colors ${filter === f.id ? 'bg-white/10 text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-300" data-mp="4fjkmdesebtw">
            {f.label}
          </button>)}
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]" data-id="element-301" data-mp="9rq2d3gzzmkc">
        <div className="hidden grid-cols-[1.2fr_1fr_1.4fr_1fr_1fr_24px] gap-4 border-b border-white/10 px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground md:grid" data-id="element-302" data-mp="5rfufl9nam2u">
          <span data-id="element-303" data-mp="3rxl9agr5msjh">Order</span>
          <span data-id="element-304" data-mp="3rxcv8ljaph6p">Date</span>
          <span data-id="element-305" data-mp="3s07ewauhixz2">Pack</span>
          <span data-id="element-306" data-mp="3mz1hhzpvqmj7">Total</span>
          <span data-id="element-307" data-mp="3my9ql2789cz9">Status</span>
          <span data-id="element-308" data-mp="3mzjn3myfmd3s" />
        </div>
        {shown.length === 0 ? <div className="p-12 text-center" data-id="element-309" data-mp="3iy7x3gbnbpv2">
            <p className="font-medium" data-id="element-310" data-mp="3izj03dx9otl4">No orders here</p>
            <p className="mt-1 text-sm text-muted-foreground" data-id="element-311" data-mp="1l7y5p74971x2">Try a different filter.</p>
          </div> : <ul className="divide-y divide-white/5" data-id="element-312" data-mp="1h7ebabb2ecem">
            {shown.map(o => <li key={o.id} data-id="element-313" data-mp="1oab59gwtutje">
                <Link to={`/orders/${o.id}`} className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1 px-5 py-4 transition-colors hover:bg-white/[0.03] focus-visible:bg-white/[0.03] focus-visible:outline-none md:grid-cols-[1.2fr_1fr_1.4fr_1fr_1fr_24px]" data-id="element-314" data-mp="1oa2npbprf01v">
                  <span className="font-mono text-sm" data-id="element-315" data-mp="1jc19p576xqx9">{o.id}</span>
                  <span className="text-right text-sm font-medium md:hidden" data-id="element-316" data-mp="15h783lkehyk1">{formatAED(o.total)}</span>
                  <span className="text-sm text-muted-foreground" data-id="element-317" data-mp="1ab34ijl0ndd3">{formatOrderDate(o.placedAt)}</span>
                  <span className="text-right md:hidden" data-id="element-318" data-mp="vmf9cef315no"><OrderStatusBadge status={o.status} data-id="element-319" data-mp="10jfg6rjracfu" /></span>
                  <span className="col-span-2 text-sm md:col-span-1" data-id="element-320" data-mp="10jyl97ob56cg">
                    {o.label} <span className="text-muted-foreground" data-id="element-321" data-mp="ml8oxu9bbap5">· {o.pillIds.length} supplements</span>
                  </span>
                  <span className="hidden text-sm font-medium md:block" data-id="element-322" data-mp="rl3gm00ww4b9">{formatAED(o.total)}</span>
                  <span className="hidden md:block" data-id="element-323" data-mp="7zbcgeuldmxf"><OrderStatusBadge status={o.status} data-id="element-324" data-mp="2ydxeb4vp905" /></span>
                  <ChevronRight className="hidden size-4 text-muted-foreground md:block" data-id="element-325" data-mp="2zytw6ar720n" />
                </Link>
              </li>)}
          </ul>}
      </div>
    </div>;
}